import React, { createContext, useContext, useCallback, useMemo, useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import useEventEmitter from '../Helpers/useEventEmitter';

const ADD_CHILD = "add";
const TRIGGER_CHILD_LOAD = "trigger";

export const PACKERY_EVENTS = {
    CHILD_ADDED: 'onAddChild',
    CHILD_LOAD_SUCCESS: 'onChildLoadSuccess',
    ALL_CHILDREN_LOADED: 'onAllChildrenLoaded'
};

const addChild = (previousState, action) => {
    if (Object.prototype.hasOwnProperty.call(previousState.components, action.key)) {
        return previousState;
    } 
    else 
    {
        var state = JSON.parse(JSON.stringify(previousState));
        var components = state.components;

        components[action.key] = { name: action.key, loaded: false };

        return state;
    }
};

const triggerChildLoad = (previousState, action) => {
    
    var state = JSON.parse(JSON.stringify(previousState));
    var components = state.components;

    if (Object.prototype.hasOwnProperty.call(components, action.key)) {
        components[action.key].loaded = true;

        return state;
    }

    return previousState;
};

const PackeryLoadingReducer = (previousState, action) => {
    switch (action.type) {
        case ADD_CHILD:
            return addChild(previousState, action);
        case TRIGGER_CHILD_LOAD:
            return triggerChildLoad(previousState, action);
        default:
            return previousState;
    }
}

const PackeryLoadingContext = createContext(null);
export const PackeryLoadingProvider = ({componentsCount, children, componentName}) => {
    const applyEmitterHandler = useEventEmitter(componentName);
    const [packeryLoadingState, packeryLoadingDispatch] = useReducer(PackeryLoadingReducer, { components: { }, componentsCount: componentsCount, allLoaded: false });

    useEffect(() => {
        if (Object.keys(packeryLoadingState.components).length == componentsCount && Object.values(packeryLoadingState.components).findIndex(x => !x.loaded) === -1)
        {
            applyEmitterHandler((emitter) => {
                emitter.emit(PACKERY_EVENTS.ALL_CHILDREN_LOADED);
            });
        }
    }, [applyEmitterHandler, packeryLoadingState.components, componentsCount]);

    const addChildFn = useCallback((componentName) => {
        packeryLoadingDispatch({
            type: ADD_CHILD,
            key: componentName
        });

        applyEmitterHandler((emitter) => {
            emitter.emit(PACKERY_EVENTS.CHILD_ADDED, componentName);
        });
    }, [applyEmitterHandler]);

    const triggerChildLoadFn = useCallback((componentName) => {
        packeryLoadingDispatch({
            type: TRIGGER_CHILD_LOAD,
            key: componentName
        });

        applyEmitterHandler((emitter) => {
            emitter.emit(PACKERY_EVENTS.CHILD_LOAD_SUCCESS, componentName);
        });
    }, [applyEmitterHandler]);

    const contextValue = useMemo(
        () => ({ 
            addChildFn, 
            triggerChildLoadFn 
        }),
        [addChildFn, triggerChildLoadFn]
    );

    return useMemo(() => (
        <PackeryLoadingContext.Provider value={contextValue}>
            {children}
        </PackeryLoadingContext.Provider>
    ), [contextValue, children]);
}

PackeryLoadingProvider.propTypes = {
    componentsCount: PropTypes.number.isRequired,
    children: PropTypes.node.isRequired,
    componentName: PropTypes.string
};

export default function usePackeryLoading() {
    const { addChildFn, triggerChildLoadFn } = useContext(PackeryLoadingContext);

    return useMemo(
        () => ({ 
            addChildFn: (componentName) => { addChildFn(componentName); }, 
            triggerChildLoadFn: (componentName) => { triggerChildLoadFn(componentName); }
        }),
        [addChildFn, triggerChildLoadFn]
    );
}